// Plan 24b Phase B, Task 1.4 — Map Overview overlay (Screen 16)
//
// Full-viewport overlay shown when the local player opens the map view.
// Displays a semi-transparent map backdrop, player markers with synthetic
// positions (Phase B placeholder), a tile-type legend, and a close hint.
//
// Toggle behaviour:
//   - If payload.toggleWiring is present, subscribe to 'toggleMapOverview' or
//     'toggleScoreboard' events (either closes this overlay back to board-hud).
//   - Otherwise fall back to KeyY / Tab keydown listeners.
//   - ESC always closes (named function reference for clean deregistration).

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { ToggleWiring } from '@client/input/toggle-wiring';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type MapOverviewPayload = BoardMountPayload & {
  toggleWiring?: ToggleWiring;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.text.secondary = #B0B8C8 */
const COLOR_TEXT_SECONDARY = '#B0B8C8';

/** color.text.muted = #707888 */
const COLOR_TEXT_MUTED = '#707888';

/** color.ui.panel-bg-alpha = rgba(30, 35, 50, 0.82) */
const PANEL_BG_ALPHA = 'rgba(30, 35, 50, 0.82)';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** border-radius.pill = 9999px */
const RADIUS_PILL = '9999px';

/** typography.size.h4 = 22px */
const H4_SIZE = '22px';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** color.tile.chest = #FFD700 */
const TILE_CHEST = '#FFD700';

/** color.tile.soul = #40B0C0 */
const TILE_SOUL = '#40B0C0';

/** color.tile.cache = #D94040 */
const TILE_CACHE = '#D94040';

/** color.tile.hazard = #D94040 */
const TILE_HAZARD = '#D94040';

// ─────────────────────────────────────────────────────────────────────────────
// Player color palette (index = Player.color % 10)
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

// ─────────────────────────────────────────────────────────────────────────────
// Legend entries
// ─────────────────────────────────────────────────────────────────────────────

const LEGEND_ENTRIES: { label: string; color: string }[] = [
  { label: 'Treasure', color: TILE_CHEST },
  { label: 'Soul Anchor', color: TILE_SOUL },
  { label: 'Weapons Cache', color: TILE_CACHE },
  { label: 'Hazard', color: TILE_HAZARD },
];

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMapOverviewScreen(ctx: ScreenMountContext<MapOverviewPayload>): ScreenHandle {
  const { host, router, payload } = ctx;
  const { state, toggleWiring } = payload;

  let disposed = false;

  // ── Root overlay ──────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-map-overview';
  root.setAttribute('data-screen-id', 'map-overview');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 150',
    'pointer-events: auto',
    'background: rgba(0, 0, 0, 0.45)',
  ].join('; ');

  // ── Top-center pill label: "Map Overview" ────────────────────────────────

  const pill = document.createElement('div');
  pill.className = 'map-overview-pill';
  pill.style.cssText = [
    'position: absolute',
    'top: 16px',
    'left: 50%',
    'transform: translateX(-50%)',
    `border-radius: ${RADIUS_PILL}`,
    'padding: 6px 20px',
    'background: rgba(0, 0, 0, 0.45)',
  ].join('; ');

  const pillLabel = document.createElement('span');
  pillLabel.className = 'map-overview-title';
  pillLabel.textContent = 'Map Overview';
  pillLabel.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H4_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'font-weight: 700',
  ].join('; ');

  pill.appendChild(pillLabel);
  root.appendChild(pill);

  // ── Player markers ────────────────────────────────────────────────────────
  // Phase B synthetic positions: left: ${10 + i * 15}%; top: 50%
  // TODO(Plan 19): replace synthetic positions with worldToScreen()

  const turnOrder = state.turnOrder;
  turnOrder.forEach((playerId, i) => {
    const player = state.players.get(playerId);
    if (!player) return;

    const colorIndex = player.color % 10;
    const markerColor = PLAYER_COLORS[colorIndex] ?? PLAYER_COLORS[0];

    // Marker wrapper (positions the circle + label together)
    const markerWrapper = document.createElement('div');
    markerWrapper.className = 'map-player-marker-wrapper';
    markerWrapper.style.cssText = [
      'position: absolute',
      `left: ${10 + i * 15}%`,
      'top: 50%',
      'transform: translate(-50%, -50%)',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 4px',
    ].join('; ');

    // Colored circle
    const circle = document.createElement('div');
    circle.className = 'map-player-marker';
    circle.style.cssText = [
      'position: relative',
      'width: 40px',
      'height: 40px',
      'min-width: 40px',
      `border-radius: ${RADIUS_PILL}`,
      `background-color: ${markerColor}`,
    ].join('; ');

    // Player name label below circle
    const nameLabel = document.createElement('div');
    nameLabel.className = 'map-player-name';
    nameLabel.textContent = player.name;
    nameLabel.style.cssText = [
      `font-size: ${CAPTION_SIZE}`,
      'color: #FFFFFF',
      'white-space: nowrap',
    ].join('; ');

    markerWrapper.appendChild(circle);
    markerWrapper.appendChild(nameLabel);
    root.appendChild(markerWrapper);
  });

  // ── Bottom-left legend panel ──────────────────────────────────────────────

  const legend = document.createElement('div');
  legend.className = 'map-overview-legend';
  legend.style.cssText = [
    'position: absolute',
    'left: 16px',
    'bottom: 48px',
    `background: ${PANEL_BG_ALPHA}`,
    `border-radius: ${RADIUS_LG}`,
    'padding: 12px 16px',
    'display: flex',
    'flex-direction: column',
    'gap: 8px',
  ].join('; ');

  for (const entry of LEGEND_ENTRIES) {
    const row = document.createElement('div');
    row.className = 'map-legend-row';
    row.style.cssText = 'display: flex; align-items: center; gap: 8px;';

    const dot = document.createElement('div');
    dot.className = 'map-legend-dot';
    dot.style.cssText = [
      'width: 10px',
      'height: 10px',
      'min-width: 10px',
      `border-radius: ${RADIUS_PILL}`,
      `background-color: ${entry.color}`,
    ].join('; ');

    const label = document.createElement('span');
    label.className = 'map-legend-label';
    label.textContent = entry.label;
    label.style.cssText = [`font-size: ${CAPTION_SIZE}`, `color: ${COLOR_TEXT_SECONDARY}`].join(
      '; ',
    );

    row.appendChild(dot);
    row.appendChild(label);
    legend.appendChild(row);
  }

  root.appendChild(legend);

  // ── Bottom-center close hint ──────────────────────────────────────────────

  const closeHint = document.createElement('div');
  closeHint.className = 'map-overview-close-hint';
  closeHint.textContent = 'Press Y / Tab to close';
  closeHint.style.cssText = [
    'position: absolute',
    'bottom: 16px',
    'left: 50%',
    'transform: translateX(-50%)',
    `font-size: ${CAPTION_SIZE}`,
    `color: ${COLOR_TEXT_MUTED}`,
  ].join('; ');

  root.appendChild(closeHint);

  // ── Mount to host ─────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── Toggle / keyboard close handlers ─────────────────────────────────────

  let unsubscribeToggle: (() => void) | null = null;

  if (toggleWiring) {
    // Primary: subscribe to toggleWiring
    unsubscribeToggle = toggleWiring.subscribe((evt) => {
      if (disposed) return;
      if (evt.name === 'toggleMapOverview' || evt.name === 'toggleScoreboard') {
        router.navigate('board-hud', payload);
      }
    });
  } else {
    // Fallback: listen for KeyY or Tab
    function handleKeyDown(e: KeyboardEvent): void {
      if (disposed) return;
      if (e.code === 'KeyY' || e.key === 'Tab') {
        e.preventDefault();
        router.navigate('board-hud', payload);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    // Store cleanup in unsubscribeToggle slot for dispose()
    unsubscribeToggle = () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }

  // ESC always closes — named function for clean deregistration
  function handleEsc(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      router.navigate('board-hud', payload);
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Unsubscribe toggle wiring (or remove keyboard fallback)
    if (unsubscribeToggle) {
      unsubscribeToggle();
      unsubscribeToggle = null;
    }

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Remove from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
